import { DialogFooter } from "@/components/ui/dialog";
import { Template } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { TemplatePreview } from "../template/shared/TemplatePreview";
import {
  extractDynamicImagesFromCanvasData,
  extractDynamicTextFromCanvasData,
  replaceDynamicTextInCanvasData,
  replaceImageInCanvasData,
} from "@/components/canvas/utils/canvasUtils";
import { useEffect, useState } from "react";
import { CanvasData } from "@/components/canvas/types/canvasTypes";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { cn } from "@/lib/utils";
import { getCheckeredBg } from "@/lib/tailwindUtils";
import { CreateFromTemplateDynamicTextsForm } from "./CreateFromTemplateDynamicTextsForm";
import { useGenerateAndUpload } from "../contentCreateFromTemplate/hooks/useGenerateAndUpload";
import { CreateFromTemplateContentPropsForm, FormSchemaType } from "./CreateFromTemplateContentPropsForm";
import { CreateFromTemplateDynamicImagesForm } from "./CreateFromTemplateDynamicImagesForm";
import { DynamicTemplateDataSourceResponseItem } from "./contentTypes";

interface Props {
  template: Template;
  onClose: () => void;
  onUploadSuccess: () => void;
}

export const CreateFromTemplatePreviewAndConfigDialogStaticTab: React.FC<Props> = ({
  template,
  onClose,
  onUploadSuccess,
}) => {
  const { t } = useTranslation();
  const canvasData = template.canvas_data;
  const [replacedImages, setReplacedImages] = useState<Record<string, string>>({});
  const [replacedTexts, setReplacedTexts] = useState<Record<string, string>>({});
  const [newCanvasData, setNewCanvasData] = useState<CanvasData>(canvasData);
  const dynamicTexts = extractDynamicTextFromCanvasData(canvasData);
  const dynamicImages = extractDynamicImagesFromCanvasData(canvasData);

  const [contentProps, setContentProps] = useState<FormSchemaType>({
    name: "",
    startDate: "",
    endDate: "",
    screenSizeId: 0,
    duration: 0,
  });
  const [contentPropsErrors, setContentPropsErrors] = useState<any>({});

  useEffect(() => {
    setNewCanvasData(canvasData);
  }, [setNewCanvasData, canvasData]);

  const getPreviewCanvasData = () => {
    let tempCanvasData = JSON.parse(JSON.stringify(newCanvasData));
    for (const [oldImageSrc, newImageSrc] of Object.entries(replacedImages)) {
      tempCanvasData = replaceImageInCanvasData(tempCanvasData, oldImageSrc, newImageSrc);
    }
    for (const [dynamicKey, newText] of Object.entries(replacedTexts)) {
      tempCanvasData = replaceDynamicTextInCanvasData(tempCanvasData, dynamicKey, newText);
    }
    return tempCanvasData;
  };

  const { generateAndUpload, isUploading } = useGenerateAndUpload({
    newCanvasData: getPreviewCanvasData(),
    template,
    onUploadSuccess,
    contentProps,
  });

  const handleImageReplace = (oldImageSrc: string, newImageSrc: string) => {
    setReplacedImages({ ...replacedImages, [oldImageSrc]: newImageSrc });
  };

  const handleDataSourceChange = (dataSource: DynamicTemplateDataSourceResponseItem[]) => {
    const newReplacedTexts: Record<string, string> = {};
    for (const item of dataSource) {
      newReplacedTexts[item.key] = String(item.value);
    }
    setReplacedTexts(newReplacedTexts);
  };

  return (
    <>
      <div className="flex items-start space-x-4">
        <div className="w-1/3">
          <div className={cn(`rounded-md overflow-hidden border h-[500px]`, getCheckeredBg())}>
            <TemplatePreview data={getPreviewCanvasData()} previewSize="full" />
          </div>
        </div>
        <div className="w-2/3">
          <div className="space-y-4">
            {dynamicImages.length > 0 && (
              <CreateFromTemplateDynamicImagesForm dynamicImages={dynamicImages} onReplace={handleImageReplace} />
            )}
            {dynamicTexts.length > 0 && (
              <CreateFromTemplateDynamicTextsForm dynamicTexts={dynamicTexts} onFormChange={handleDataSourceChange} />
            )}
            <CreateFromTemplateContentPropsForm
              onFormValuesChange={setContentProps}
              onFormErrorsChange={setContentPropsErrors}
            />
          </div>
        </div>
      </div>

      <DialogFooter className="mt-6">
        <Button disabled={isUploading} onClick={onClose} variant="outline">
          {t("common.cancel")}
        </Button>
        <Button onClick={generateAndUpload} disabled={isUploading || Object.keys(contentPropsErrors).length > 0}>
          {t("content.generateContent")} {isUploading && <LoadingSmall />}
        </Button>
      </DialogFooter>
    </>
  );
};
